import { Dispatch, Reducer } from 'react';
import { action, on, props, reducer, union } from 'ts-action';
import { CidCvnListResponse, CidCvnCheckResult } from '@packages/models/api/cid-cvn';

export enum CidCvnActionsList {
    CLEAR_CID_CVN_DATA = 'CLEAR_CID_CVN_DATA',
    SET_CID_CVN_ELIGIBILITY = 'SET_CID_CVN_ELIGIBILITY',
    SET_CID_CVN_LIST = 'SET_CID_CVN_LIST',
    SET_LOADING = 'SET_LOADING',
    SET_ERRORS = 'SET_ERRORS',
    SET_SELECTED_REPORT_HEADER = 'SET_SELECTED_REPORT_HEADER',
    SET_CID_CVN_CHECK_BLOCK = 'SET_CID_CVN_CHECK_BLOCK',
}

export const cidCvnActions = {
    clearCidCvnData: action(CidCvnActionsList.CLEAR_CID_CVN_DATA),
    setCidCvnEligibility: action(CidCvnActionsList.SET_CID_CVN_ELIGIBILITY, props<{ cidCvnEligible: boolean }>()),
    setCidCvnList: action(CidCvnActionsList.SET_CID_CVN_LIST, props<{ cidCvnList: CidCvnListResponse }>()),
    setLoading: action(CidCvnActionsList.SET_LOADING, props<{ isLoading: boolean }>()),
    setErrors: action(CidCvnActionsList.SET_ERRORS, props<{ errors: string[] }>()),
    setSelectedReportHeader: action(
        CidCvnActionsList.SET_SELECTED_REPORT_HEADER,
        props<{
            selectedReportHeader: {
                id: string | null;
                reportName: string | null;
                result: CidCvnCheckResult | null;
            };
        }>()
    ),
    setCidCvnCheckBlock: action(CidCvnActionsList.SET_CID_CVN_CHECK_BLOCK, props<{ vinLockedout: boolean }>()),
};

const actionsUnion = union(...Object.values(cidCvnActions));
type CidCvnActions = typeof actionsUnion.actions;

export type CidCvnActionDispatcher = Dispatch<CidCvnActions>;

export interface CidCvnState {
    cidCvnEligible: boolean;
    cidCvnBlocked: boolean;
    cidCvnList: CidCvnListResponse | [];
    cidCvnCount: number;
    isLoading: boolean;
    errors: string[];
    selectedReportHeader: {
        id: string | null;
        reportName: string | null;
        result: CidCvnCheckResult | null;
    };
}

export const initialState: CidCvnState = {
    cidCvnEligible: false,
    cidCvnBlocked: false,
    cidCvnList: [],
    cidCvnCount: 0,
    isLoading: false,
    errors: [],
    selectedReportHeader: {
        id: null,
        reportName: null,
        result: null,
    },
};

export const cidCvnReducer: Reducer<CidCvnState, CidCvnActions> = reducer(
    initialState,
    on(cidCvnActions.clearCidCvnData, (state) => ({
        ...state,
        cidCvnEligible: false,
        cidCvnList: [],
        cidCvnCount: 0,
        isLoading: false,
        errors: [],
        selectedReportHeader: {
            id: null,
            reportName: null,
            result: null,
        },
        cidCvnBlocked: false,
    })),
    on(cidCvnActions.setCidCvnEligibility, (state, { cidCvnEligible }) => ({
        ...state,
        cidCvnEligible,
    })),
    on(cidCvnActions.setCidCvnList, (state, { cidCvnList }) => ({
        ...state,
        cidCvnList,
        cidCvnCount: Array.isArray(cidCvnList) ? cidCvnList.length : 0,
    })),
    on(cidCvnActions.setLoading, (state, { isLoading }) => ({
        ...state,
        isLoading,
    })),
    on(cidCvnActions.setErrors, (state, { errors }) => ({
        ...state,
        errors,
    })),
    on(cidCvnActions.setSelectedReportHeader, (state, { selectedReportHeader }) => ({
        ...state,
        selectedReportHeader,
    })),
    on(cidCvnActions.setCidCvnCheckBlock, (state, { vinLockedout }) => ({
        ...state,
        cidCvnBlocked: !!vinLockedout,
    }))
);
